import { useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { campaignIdFromRunId } from "utils/calibrationCampaign";

function formatTimestamp(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString();
}

/**
 * Compact, collapsible browsable history of past/active runs for one
 * controller - lets the operator look at an older run's charts *while*
 * staying on the Experiment page, rather than navigating away from the
 * charts to find one. Collapsed by default and visually secondary to the
 * run-start form/charts above it, per the page's own layout priority.
 *
 * Purely presentational: the `runs`/`total` it renders and what happens on
 * "View"/"Load more" all live in useExperiment (viewRun/loadMoreRuns), so
 * this component owns no fetching of its own beyond its collapsed/expanded
 * UI state.
 */
// One row per experiment: a calibration campaign's condition runs (calib-<id>-c<n>-...) are grouped into one row, so
// a two-condition campaign does not look like two separate, unfinished runs. Its row opens the newest condition (the
// page then shows the whole campaign). Runs arrive newest first, so the first one seen is the newest.
export function groupRuns(runs) {
  const rows = [];
  const byCampaign = {};
  for (const run of runs) {
    const campaignId = campaignIdFromRunId(run.run_id);
    if (campaignId === null) {
      rows.push({ key: run.run_id, run, runs: [run] });
      continue;
    }
    if (!byCampaign[campaignId]) {
      byCampaign[campaignId] = { key: `campaign-${campaignId}`, campaignId, run, runs: [] };
      rows.push(byCampaign[campaignId]);
    }
    const row = byCampaign[campaignId];
    row.runs.push(run);
    row.started_at = run.started_at;                       // the oldest condition's start
    row.is_active = row.is_active || run.is_active;
  }
  return rows;
}

export default function RunHistoryList({ runs, total, loading, viewedRunId, onView, onLoadMore }) {
  const [expanded, setExpanded] = useState(false);
  const rows = groupRuns(runs);

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <button
        type="button"
        onClick={() => setExpanded((value) => !value)}
        className="flex w-full items-center justify-between text-left"
        aria-expanded={expanded}
      >
        <span className="text-sm font-semibold text-slate-900">
          Past runs{total ? ` (${total})` : ""}
        </span>
        {expanded ? (
          <ChevronUp className="h-4 w-4 text-slate-500" />
        ) : (
          <ChevronDown className="h-4 w-4 text-slate-500" />
        )}
      </button>

      {expanded && (
        <div className="mt-3">
          {runs.length === 0 ? (
            <p className="text-sm text-slate-500">
              {loading ? "Loading…" : "No runs recorded yet."}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead>
                  <tr className="text-slate-400">
                    <th className="py-1 pr-3 font-bold uppercase tracking-wide">Run ID</th>
                    <th className="py-1 pr-3 font-bold uppercase tracking-wide">Profile</th>
                    <th className="py-1 pr-3 font-bold uppercase tracking-wide">Started</th>
                    <th className="py-1 pr-3 font-bold uppercase tracking-wide">Ended</th>
                    <th className="py-1 pr-3" />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => {
                    const run = row.run;
                    const campaign = row.campaignId !== undefined;
                    const viewed = row.runs.some((r) => r.run_id === viewedRunId);
                    const active = campaign ? row.is_active : run.is_active;
                    const name = (run.profile_name || "").replace(/^Calibration: /, "");
                    return (
                      <tr key={row.key} className={`border-t border-slate-100 ${viewed ? "bg-blue-50" : ""}`}>
                        <td className="py-1.5 pr-3 font-mono">
                          {campaign ? `calib-${row.campaignId}` : run.run_id}
                          {campaign && (
                            <span className="ml-2 rounded-full bg-blue-100 px-1.5 py-0.5 font-sans text-[10px] font-bold text-blue-800">
                              Calibration campaign · {row.runs.length} condition{row.runs.length === 1 ? "" : "s"}
                              {active ? " so far" : ""}
                            </span>
                          )}
                        </td>
                        <td className="py-1.5 pr-3">{campaign ? name || "Calibration" : run.profile_name || "—"}</td>
                        <td className="py-1.5 pr-3">{formatTimestamp(campaign ? row.started_at : run.started_at)}</td>
                        <td className="py-1.5 pr-3">{active ? "Running" : formatTimestamp(run.ended_at)}</td>
                        <td className="py-1.5 pr-3 text-right">
                          <button
                            type="button"
                            onClick={() => onView(run.run_id)}
                            className="rounded-lg border border-slate-200 bg-white px-2 py-1 text-[11px] font-bold text-slate-700 hover:bg-slate-50"
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {runs.length < total && (
                <button
                  type="button"
                  onClick={onLoadMore}
                  disabled={loading}
                  className="mt-2 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-bold text-slate-700 disabled:opacity-50"
                >
                  {loading ? "Loading…" : "Load more"}
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
